import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

import * as API from '../../api';

/**
 * Social-login callback — premium redesign (was Material-UI + the legacy
 * banner/card, which rendered unstyled since the global BEM stylesheet is no
 * longer imported anywhere).
 *
 * Route: /connection/:provider — the OAuth provider redirects here with its
 * query string. Behaviour is preserved exactly:
 *   1. POST the callback query to API.socialLoginCallback(provider, '?...')
 *   2. If the provider returned both email AND phone → redirect to `intended`
 *      (or "/") after 4s.
 *   3. If either is missing → show a form to collect just the missing one(s),
 *      PUT via API.ChangeUserInfo(form, token), then redirect after 4s.
 *   4. On error → show it, then redirect after 3s.
 */
/**
 * Temporarily off: after a social login, don't stop to collect a missing
 * phone number — send the user straight on.
 *
 * Google returns an email but never a phone, so every Google sign-in hit this
 * step. Set back to true to restore it; the form and its submit handler are
 * left intact, only the gate changes.
 *
 * Note that accounts created while this is false have no phone number, and
 * anything downstream that assumes one (SMS verification, partners calling a
 * customer about a booking) will find it empty.
 */
const REQUIRE_PROFILE_COMPLETION = false;

export default function Connection() {
  const intended = localStorage.getItem('intended');
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const route = useRouteMatch();
  const provider = route.params.provider;

  const [loginHelper, setLoginHelper] = React.useState({ type: '', message: '' });
  const [loadingLoginSocial, setLoadingLoginSocial] = React.useState(false);
  const [token, setToken] = React.useState('');
  const [auth, setAuth] = React.useState({});
  const [emailHelper, setEmailHelper] = React.useState('');
  const [phoneHelper, setPhoneHelper] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const submitUserUpdate = (event) => {
    event.preventDefault();
    setLoading(true);
    setEmailHelper('');
    setPhoneHelper('');

    const formData = new FormData(event.target);
    const form = {};
    if (!auth.email) Object.assign(form, { email: formData.get('email') });
    if (!auth.phone) Object.assign(form, { phone: formData.get('phone') });

    API.ChangeUserInfo(form, token)
      .then(() => {
        enqueueSnackbar('Vos informations ont été mises à jour avec succès', { variant: 'success' });
        setTimeout(() => {
          window.location.href = intended ?? '/';
        }, 4000);
      })
      .catch((err) => {
        if (err.status === 422) {
          enqueueSnackbar(err.data.message, { variant: 'error' });
          if (err.data.errors.phone) setPhoneHelper(err.data.errors.phone);
          if (err.data.errors.email) setEmailHelper(err.data.errors.email);
        }
      })
      .then(() => setLoading(false));
  };

  React.useEffect(() => {
    if (!provider) return;

    const queryString = new URLSearchParams(window.location.search).toString();

    setLoadingLoginSocial(true);
    API.socialLoginCallback(provider, '?' + queryString)
      .then((res) => {
        setLoginHelper({
          type: 'success',
          message: `Bienvenue 👋 <b>${res.data.user.name}</b><br/><br/>Vous serez dirigé dans un instant 😃…`,
        });
        setAuth(res.data.user);
        setToken(res.data.access_token);
        // Let the navbar pick up the new session.
        window.dispatchEvent(new Event('tafwija-auth-changed'));

        // With profile completion off, always move on — otherwise a user
        // without a phone number would sit on this screen indefinitely,
        // since the form that used to collect it is no longer rendered.
        const complete = res.data.user.phone && res.data.user.email;

        if (complete || !REQUIRE_PROFILE_COMPLETION) {
          setTimeout(
            () => {
              window.location.href = intended ?? '/';
            },
            REQUIRE_PROFILE_COMPLETION ? 4000 : 1500
          );
        }
      })
      .catch((err) => {
        setLoginHelper({ type: 'error', message: err?.data?.errors });
        setTimeout(() => {
          window.location.href = intended ?? '/';
        }, 3000);
      })
      .then(() => setLoadingLoginSocial(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [0]);

  // Provider returned an incomplete profile — ask only for what's missing.
  // Suppressed while REQUIRE_PROFILE_COMPLETION is false: social sign-in goes
  // straight through instead of stopping to collect a phone number.
  const needsProfile =
    REQUIRE_PROFILE_COMPLETION && Object.keys(auth).length && (!auth.phone || !auth.email);

  return (
    <div className="relative min-h-[70vh] overflow-hidden bg-surface-muted">
      {/* Brand-tinted backdrop, matching the partner/admin auth pages */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-50 via-surface-muted to-surface-muted" />
      <div aria-hidden className="pointer-events-none absolute -top-32 -start-24 h-96 w-96 rounded-full bg-brand-500/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -end-24 h-96 w-96 rounded-full bg-accent-500/20 blur-3xl" />

      <div className="relative mx-auto flex w-full max-w-container flex-col items-center px-4 py-12 sm:px-6 sm:py-16">
        <div className="w-full max-w-md">
          <div className="rounded-3xl border border-white/70 bg-white/90 p-6 shadow-elevated backdrop-blur-sm sm:p-8">

            {/* ---- Connecting ---- */}
            {loadingLoginSocial ? (
              <div className="flex flex-col items-center py-6 text-center">
                <span className="inline-block h-10 w-10 animate-spin rounded-full border-[3px] border-brand-500/25 border-t-brand-500" />
                <p className="mt-5 font-display text-lg font-semibold text-ink">Connexion en cours…</p>
                <p className="mt-1 text-sm text-ink-muted">Merci de patienter un instant.</p>
              </div>
            ) : null}

            {/* ---- Complete the profile ---- */}
            {!loadingLoginSocial && loginHelper.message && needsProfile ? (
              <>
                <div className="text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                    <UserCircleIcon className="h-6 w-6" />
                  </span>
                  <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-ink">
                    Presque terminé
                  </h1>
                  <p className="mt-1.5 text-sm text-ink-muted">
                    Complétez ces informations pour activer votre compte.
                  </p>
                </div>

                <form onSubmit={submitUserUpdate} method="POST" className="mt-6 space-y-4">
                  {!auth.email ? (
                    <Field
                      name="email"
                      type="email"
                      label="Adresse e-mail"
                      placeholder="vous@exemple.com"
                      icon={EnvelopeIcon}
                      error={emailHelper}
                      disabled={loading}
                      autoComplete="email"
                    />
                  ) : null}

                  {!auth.phone ? (
                    <Field
                      name="phone"
                      type="tel"
                      label="Téléphone"
                      placeholder="+212 6 12 34 56 78"
                      icon={PhoneIcon}
                      error={phoneHelper}
                      disabled={loading}
                      autoComplete="tel"
                    />
                  ) : null}

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand-500 px-6 font-semibold text-white shadow-card transition-colors hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    ) : (
                      'Enregistrer'
                    )}
                  </button>
                </form>
              </>
            ) : null}

            {/* ---- Success / error message ---- */}
            {!loadingLoginSocial && loginHelper.message && !needsProfile ? (
              <div className="py-2 text-center">
                <span
                  className={`mx-auto flex h-12 w-12 items-center justify-center rounded-2xl ${
                    loginHelper.type === 'success'
                      ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-rose-50 text-rose-500'
                  }`}
                >
                  {loginHelper.type === 'success' ? (
                    <CheckCircleIcon className="h-6 w-6" />
                  ) : (
                    <ExclamationCircleIcon className="h-6 w-6" />
                  )}
                </span>
                <div
                  className="mt-4 text-sm leading-relaxed text-ink-muted [&_b]:font-semibold [&_b]:text-ink"
                  dangerouslySetInnerHTML={{ __html: loginHelper.message }}
                />
              </div>
            ) : null}

            {/* ---- No provider in the URL (direct visit) ---- */}
            {!loadingLoginSocial && !loginHelper.message ? (
              <div className="py-4 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                  <UserCircleIcon className="h-6 w-6" />
                </span>
                <h1 className="mt-4 font-display text-xl font-extrabold tracking-tight text-ink">
                  Connexion
                </h1>
                <p className="mt-1.5 text-sm text-ink-muted">
                  Utilisez le bouton « Se connecter » en haut de la page pour accéder à votre compte.
                </p>
                <button
                  type="button"
                  onClick={() => history.push('/')}
                  className="mt-5 inline-flex h-11 items-center justify-center rounded-full bg-brand-500 px-6 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-600"
                >
                  Retour à l'accueil
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- presentational helper (mirrors Connection/Reset.js) ---- */

function Field({ name, type = 'text', label, placeholder, icon: Icon, error, disabled, autoComplete }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-subtle">
        {label}
      </label>
      <div
        className={`flex h-12 items-center gap-2.5 rounded-full border bg-white ps-4 pe-4 transition ${
          error
            ? 'border-rose-300 focus-within:border-rose-400 focus-within:ring-2 focus-within:ring-rose-400/15'
            : 'border-slate-200 hover:border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/15'
        }`}
      >
        <Icon className="h-4 w-4 shrink-0 text-ink-subtle" />
        <input
          name={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className="min-w-0 flex-1 bg-transparent text-sm font-medium text-ink placeholder:text-ink-subtle outline-none disabled:cursor-not-allowed disabled:opacity-60"
        />
      </div>
      {error ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-rose-600">
          <ExclamationCircleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
